import {
  AfterViewInit,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { environment } from '../../../../environments/environment.local';
import {
  AlertService,
  AlertType,
  CardComponent,
  ColumnConfig,
  GenericButtonComponent,
  ConfirmationDialogService,
  DataListComponent,
  DataListItem,
  DynamicFieldDirective,
  DocumentAction,
  DocumentActionEvent,
  DocumentConfig,
  DocumentDisplayMode,
  DocumentItem,
  FieldType,
  GenericDocumentationComponent,
  GenericTab,
  GenericTabConfig,
  GenericTableComponent,
  GenericTabsComponent,
  GlobalAction,
  HeaderConfigurationService,
  LoaderService,
  ModalFieldConfig,
  MoreDataConfig,
  TableAction,
  TableActionConfig,
  ButtonType,
} from '@solcre-org/core-ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DataStoreService } from '../../../shared/services/data-store.service';
import { BookingItemModel } from '../../../shared/models/booking-item.model';
import { ActivatedRoute, Router } from '@angular/router';
import { ClientRateModel } from '../../../shared/models/client-rate.model';
import { CargoTypeModel } from '../../../shared/models/cargo-type.model';
import { cargoTypeUriTemplate } from '../../../shared/templates/cargoTypeUriTemplate';
import { clientRateUriTemplate } from '../../../shared/templates/clientRateUriTemplate';
import { FormGroup, Validators } from '@angular/forms';
import { BookingModel } from '../../../shared/models/booking.model';
import { bookingUriTemplate } from '../../../shared/templates/bookingUriTemplate';
import { VoyageService } from '../../../shared/services/voyage.service';
import { DateUtils } from '../../../shared/utils/date.utils';
import { clientNameTemplate } from '../../../shared/templates/clientNameTemplate';
import { portNameTemplate } from '../../../shared/templates/portNameTemplate';
import { voyageNameTemplate } from '../../../shared/templates/voyageNameTemplate';
import { VoyageModel } from '../../../shared/models/voyage.model';
import { getPaymentTermLabel } from '../../../shared/data/paymentTerms';
import { Subscription } from 'rxjs';
import { BookingService } from '../../../shared/services/booking.service';
import { BookingAttachmentModel } from '../../../shared/models/booking-attachment.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ArrayUtility, ApiService, ApiResponseModel } from '@solcre-org/core';
import { EIGHT_DIGITS_ALLOW_ZERO_PATTERN_VALIDATOR } from '../../../shared/validators/pattern-validators';
import { RateTerms } from '../../../shared/enums/rate-terms.enum';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-bookings-view',
  imports: [
    DynamicFieldDirective,
    GenericTableComponent,
    GenericButtonComponent,
    TranslateModule,
    CardComponent,
    DataListComponent,
    GenericTabsComponent,
    GenericDocumentationComponent,
  ],
  templateUrl: './bookings-view.html',
})
export class BookingsView implements OnInit, AfterViewInit {
  //Services
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private headerConfigurationService = inject(HeaderConfigurationService);
  private translateService: TranslateService = inject(TranslateService);
  private dataStoreService: DataStoreService = inject(DataStoreService);
  private voyageService: VoyageService = inject(VoyageService);
  private loaderService: LoaderService = inject(LoaderService);
  private alertService: AlertService = inject(AlertService);
  private apiService: ApiService = inject(ApiService);
  private bookingService: BookingService = inject(BookingService);
  private confirmService: ConfirmationDialogService = inject(
    ConfirmationDialogService
  );

  //Models
  bookingId = signal<number | null>(null, {});
  bookingCode = signal<string | null>(null);
  booking = signal<BookingModel | null>(null);
  bookingDataList = signal<DataListItem[]>([]);
  bookingsItems = signal<BookingItemModel[]>([]);
  suggestingRevenueItemId = signal<string | null>(null);
  subscription: Subscription = new Subscription();
  bookingAttachments = signal<DocumentItem[]>([]);
  readonly bookingItemModalForm = signal<FormGroup | null>(null);
  readonly ButtonType = ButtonType;

  /**
   * Derives the route summary displayed in the booking header.
   *
   * Returns `null` when no booking is loaded, otherwise provides:
   * - `directPath` — origin → destination label (no transshipment).
   * - `transshipmentPath` — origin → hub → destination label.
   * - `firstLegLabel` / `secondLegLabel` — voyage display names for each leg.
   * - `requiresTransshipment` — whether to show the transshipment path variant.
   */
  routeSummary = computed(() => {
    const booking = this.booking();
    if (!booking) {
      return null;
    }

    const origin = booking.originPortDetails?.fullName ?? booking.originPortDetails?.portCode ?? '-';
    const destination = booking.destinationPortDetails?.fullName ?? booking.destinationPortDetails?.portCode ?? '-';
    const transshipment = booking.transshipmentPortDetails?.fullName ?? booking.transshipmentPortDetails?.portCode ?? '-';

    const formatLeg = (voyage?: VoyageModel) => {
      if (!voyage) return 'TBN';
      const vessel = voyage.vesselDetails?.vesselName;
      const ref = voyage.getReference();
      if (vessel && ref) return `${vessel} - ${ref}`;
      return vessel || ref || 'TBN';
    };

    return {
      requiresTransshipment: booking.connectionType !== 'direct',
      connectionTypeLabelKey: `bookingsView.connectionTypes.${booking.connectionType}`,
      directPath: `${origin} → ${destination}`,
      transshipmentPath: `${origin} → ${transshipment} → ${destination}`,
      firstLegLabel: this.voyageDisplayName(booking.voyageDetails),
      transshipmentLegs: `Leg 1: ${formatLeg(booking.voyageDetails)} / Leg 2: ${formatLeg(booking.secondVoyageDetails)}`
    };
  });

  //Others
  @ViewChild('chasisTemplate', { static: true })
  chasisTemplate!: TemplateRef<{
    $implicit: BookingItemModel;
    column: ColumnConfig<BookingItemModel>;
  }>;
  @ViewChild('revenueFieldTemplate', { static: true })
  revenueFieldTemplate!: TemplateRef<any>;
  @ViewChild('clientRateTemplate', { static: true })
  clientRateTemplate!: TemplateRef<any>;

  //Configs
  endpoint = computed(() => {
    return environment.API.BOOKING_ITEMS_URI.replace(
      ':id',
      this.bookingId()?.toString() ?? ''
    );
  });
  columns: ColumnConfig<BookingItemModel>[] = [];

  modalFields = computed<ModalFieldConfig<BookingItemModel>[]>(
    () => [
      {
        key: 'cargoType',
        label: 'bookingsView.cargoType',
        placeholder: 'bookingsView.cargoTypePlaceholder',
        type: FieldType.SELECT,
        searchable: true,
        readonly: false,
        defaultValue: '',
        options: this.dataStoreService
          .selectAll<CargoTypeModel>('cargoTypes')()
          .map((cargoType: CargoTypeModel) => ({
            value: cargoType.id,
            label: cargoType.name,
          })),
        validators: [Validators.required],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
        },
        customViewTemplate: (cargoTypeId: string) => {
          return this.cargoTypeName(cargoTypeId);
        },
      },
      {
        key: 'description',
        label: 'bookingsView.description',
        placeholder: 'bookingsView.descriptionPlaceholder',
        type: FieldType.TEXT,
        readonly: false,
        validators: [Validators.required],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
        },
      },
      {
        key: 'quantity',
        label: 'bookingsView.quantity',
        placeholder: 'bookingsView.quantityPlaceholder',
        type: FieldType.NUMBER,
        defaultValue: 1,
        readonly: false,
        validators: [Validators.required],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
        },
      },
      {
        key: 'chasis',
        label: 'bookingsView.chasis',
        placeholder: 'bookingsView.chasisPlaceholder',
        type: FieldType.TEXTAREA,
        readonly: false,
      },
      {
        key: 'maker',
        label: 'bookingsView.maker',
        placeholder: 'bookingsView.makerPlaceholder',
        type: FieldType.TEXT,
        readonly: false,
        validators: [Validators.required],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
        },
      },
      {
        key: 'vinNumber',
        label: 'bookingsView.vinNumber',
        placeholder: 'bookingsView.vinNumberPlaceholder',
        type: FieldType.TEXT,
        readonly: false,
        validators: [Validators.maxLength(17)],
        errorMessages: {
          maxlength: this.translateService.instant('validators.maxLength', { maxLength: 17 }),
        },

      },
      {
        key: 'length',
        label: 'bookingsView.length',
        placeholder: 'bookingsView.lengthPlaceholder',
        type: FieldType.NUMBER,
        readonly: false,
        validators: [Validators.required, Validators.min(0.01)],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
          min: this.translateService.instant('validators.min', { min: 0.01 }),
        },
      },
      {
        key: 'width',
        label: 'bookingsView.width',
        placeholder: 'bookingsView.widthPlaceholder',
        type: FieldType.NUMBER,
        readonly: false,
        validators: [Validators.required, Validators.min(0.01)],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
          min: this.translateService.instant('validators.min', { min: 0.01 }),
        },
      },
      {
        key: 'height',
        label: 'bookingsView.height',
        placeholder: 'bookingsView.heightPlaceholder',
        type: FieldType.NUMBER,
        readonly: false,
        validators: [Validators.required, Validators.min(0.01)],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
          min: this.translateService.instant('validators.min', { min: 0.01 }),
        },
      },
      {
        key: 'totalCubicMeters',
        label: 'bookingsView.totalCubicMeters',
        type: FieldType.NUMBER,
        readonly: true,
        defaultValue: 0,
        dynamicValue: (item: BookingItemModel) => {
          return this.voyageService
            .bookingItemRecalculateTotalCubicMeters(item)
            .toFixed(2);
        },
      },
      {
        key: 'unitWeight',
        label: 'bookingsView.unitWeight',
        placeholder: 'bookingsView.unitWeightPlaceholder',
        type: FieldType.NUMBER,
        readonly: false,
        validators: [Validators.required, Validators.min(0.01)],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
          min: this.translateService.instant('validators.min', { min: 0.01 }),
        },
      },
      {
        key: 'terms',
        label: 'bookingsView.terms',
        placeholder: 'bookingsView.termsPlaceholder',
        type: FieldType.SELECT,
        readonly: false,
        defaultValue: '',
        validators: [Validators.required],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
        },
        options: Object.values(RateTerms).map((term) => ({
          value: term,
          label: term,
        })),
      },
      this.revenueFieldConfig(),
    ] as ModalFieldConfig<BookingItemModel>[]
  );

  private revenueFieldBaseConfig(): ModalFieldConfig<BookingItemModel> {
    return {
      key: 'revenue',
      label: 'bookingsView.revenue',
      placeholder: 'bookingsView.revenuePlaceholder',
      type: FieldType.TEXT,
      defaultValue: '0.00',
      readonly: false,
      validators: [Validators.required, EIGHT_DIGITS_ALLOW_ZERO_PATTERN_VALIDATOR],
      errorMessages: {
        required: this.translateService.instant('validators.required'),
        pattern: this.translateService.instant('validators.digitsdecimalPattern', {
          digits: 8,
          decimals: 2,
        }),
      },
    };
  }

  revenueFieldConfig(): ModalFieldConfig<BookingItemModel> {
    return {
      ...this.revenueFieldBaseConfig(),
      customViewTemplate: this.revenueFieldTemplate,
    };
  }

  revenueInputFieldConfig(): ModalFieldConfig<BookingItemModel> {
    return this.revenueFieldBaseConfig();
  }
  globalActions: GlobalAction<BookingItemModel>[] = [
    {
      label: this.translateService.instant('bookingsView.backButton'),
      icon: 'icon-arrow-left',
      callback: () => {
        this.router.navigate(['/app/bookings']);
      },
      class: 'c-btn--stroked',
    },
  ];
  actions: TableActionConfig[] = [
    { action: TableAction.VIEW, isExtra: false },
    { action: TableAction.EDIT, isExtra: false },
    { action: TableAction.DELETE, isExtra: true },
    { action: TableAction.CREATE, isExtra: false },
  ];
  moreData: MoreDataConfig<any>[] = [];

  private activeTabId = signal<string>('booking_items');
  tabs = computed<GenericTab[]>(() => [
    {
      id: 'booking_items',
      translationKey: 'bookingsView.booking_items',
      active: this.activeTabId() === 'booking_items',
    },
    {
      id: 'attachments',
      translationKey: 'bookingsView.attachments',
      active: this.activeTabId() === 'attachments',
    },
  ]);
  tabsConfig = computed<GenericTabConfig>(() => ({
    tabs: this.tabs(),
    containerClass: 'c-tabs',
    listClass: 'c-tabs__list',
    itemClass: 'c-tabs__item',
    linkClass: 'c-tabs__link',
    activeClass: 'is-active',
    disabledClass: 'disabled',
  }));
  activeTab = computed<GenericTab | null>(
    () => this.tabs().find((tab) => tab.id === this.activeTabId()) || null
  );
  activeTabIdValue = computed<string | null>(
    () => this.activeTab()?.id || null
  );
  attachmentsConfig = signal<DocumentConfig>({
    displayMode: DocumentDisplayMode.GRID,
    showUploadDate: true,
    showUploadedBy: false,
    showDescription: false,
    allowDownload: true,
    allowDelete: true,
    allowHelp: false,
    customDateFormat: 'dd/MM/yyyy HH:mm',
    emptyStateMessage: 'documentation.noDocuments',
    emptyStateIcon: 'icon-file-not-found',
  });
  attachmentsPermissions: any = { canDelete: true, canDownload: true };

  portsModelFactory = (data: Partial<BookingItemModel>): BookingItemModel => {
    return new BookingItemModel().fromJSON(data);
  };

  constructor() {
    const id = this.activatedRoute.snapshot.params['id'];
    const bookingCode = this.activatedRoute.snapshot.queryParams['bookingCode'];

    //Load more data
    this.moreData = [
      {
        key: 'cargoTypes',
        endpoint: environment.API.CARGO_TYPES_URI,
        model: CargoTypeModel,
      },
      {
        key: 'booking',
        endpoint: bookingUriTemplate(id),
        model: BookingModel,
        isSingleObject: true,
      },
    ];

    //Load Data
    if (id) {
      this.bookingId.set(id);
    }
    if (bookingCode) {
      this.bookingCode.set(bookingCode);
    }
  }

  bookingsItemsModelFactory = (
    data: Partial<BookingItemModel>
  ): BookingItemModel => {
    return new BookingItemModel().fromJSON(data);
  };

  //Components events
  ngOnInit() {
    this.headerConfigurationService.setEndpointHeaders(this.endpoint(), {
      patch: [{ 'Content-Type': 'application/merge-patch+json' }],
    });
  }

  ngAfterViewInit(): void {
    this.columns = [
      {
        label: 'bookingsView.quantity',
        key: 'quantity',
      },
      {
        label: 'bookingsView.cargoType',
        key: 'cargoType',
        format: (cargoTypeId: string) => {
          return this.cargoTypeName(cargoTypeId);
        },
      },
      {
        label: 'bookingsView.description',
        key: 'description',
      },
      {
        label: 'bookingsView.chasis',
        key: 'chasis',
        template: this.chasisTemplate,
      },
      {
        label: 'bookingsView.maker',
        key: 'maker',
      },
      {
        label: 'bookingsView.vinNumber',
        key: 'vinNumber',
      },
      {
        label: 'bookingsView.dimensions',
        key: 'dimensions',
      },
      {
        label: 'bookingsView.unitWeight',
        key: 'unitWeight',
        format: (tons: number) => {
          return Number(tons ?? 0).toFixed(2) + ' tons';
        },
      },
      {
        label: 'bookingsView.totalLinearMeters',
        key: 'totalLinearMeters',
        format: (lm: number) => {
          return Number(lm ?? 0).toFixed(2);
        },
      },
      {
        label: 'bookingsView.totalCubicMeters',
        key: 'totalCubicMeters',
        format: (cbm: number) => {
          return Number(cbm ?? 0).toFixed(2);
        },
      },
      {
        label: 'bookingsView.terms',
        key: 'terms',
      },
      {
        label: 'bookingsView.revenue',
        key: 'revenue',
        format: (revenue: number) => `$ ${Number(revenue ?? 0).toFixed(2)}`
      },
      {
        label: 'bookingsView.linkedRate',
        key: 'clientRate',
        template: this.clientRateTemplate,
      },
    ];
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onDataChanges(item?: BookingItemModel): void {
    this.generateBookingDetailsData();
  }
  onDataFetched(items: BookingItemModel[]): void {
    this.bookingsItems.set(items ?? []);
    this.generateBookingDetailsData();
  }

  onBeforeSave(payload: { data: BookingItemModel }): void {
    if (payload && payload.data && typeof payload.data.chasis === 'string') {
      const chasisStr: string = payload.data.chasis;
      const chasis: string[] = chasisStr.split(',');
      payload.data.chasis = chasis;
    }
  }

  resolveRevenueErrors(contextErrors: string[] = []): string[] {
    if (contextErrors.length > 0) {
      return contextErrors;
    }

    const control = this.bookingItemModalForm()?.get('revenue');
    if (!control || !control.errors || (!control.touched && !control.dirty)) {
      return [];
    }

    const revenueFieldConfig = this.revenueFieldBaseConfig();
    return Object.keys(control.errors).map((key) =>
      revenueFieldConfig.errorMessages?.[key] ?? this.translateService.instant('errors.generic')
    );
  }

  syncRevenueField(bookingItem: BookingItemModel | null, value: any, touch = false): void {
    if (!bookingItem) return;

    bookingItem.revenue = value;

    const control = this.bookingItemModalForm()?.get('revenue');
    if (!control) return;

    control.setValue(value, { emitEvent: false });
    control.markAsDirty();

    if (touch) {
      control.markAsTouched();
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  suggestRevenue(event: Event, bookingItem: BookingItemModel | null): void {
    event.preventDefault();
    if (!bookingItem?.cargoType) {
      return;
    }

    this.suggestRateForBookingItem(bookingItem);
  }

  isSuggestingRevenue(bookingItem: BookingItemModel | null): boolean {
    if (!bookingItem) {
      return false;
    }

    return this.suggestingRevenueItemId() === String(this.getBookingItemIdentifier(bookingItem));
  }

  onMoreDataLoaded(event: Map<string, any[]>): void {
    if (!event) {
      return;
    }

    const booking = event.get('booking');
    if (Array.isArray(booking) && booking.length > 0) {
      this.booking.set(<BookingModel>booking[0]);
      this.generateBookingDetailsData();
      this.generateAttachmentsList();
    }

    const cargoTypes = event.get('cargoTypes');
    if (Array.isArray(cargoTypes)) {
      this.dataStoreService.hydrate<CargoTypeModel>(
        'cargoTypes',
        cargoTypes.map((cargoType: CargoTypeModel) => {
          cargoType.id = cargoTypeUriTemplate(cargoType.id);
          return cargoType;
        })
      );
    }
  }

  onTabChange(tab: GenericTab): void {
    this.activeTabId.set(tab.id);
  }

  onAttachmentsActionTriggered(event: DocumentActionEvent): void {
    switch (event.action) {
      case DocumentAction.DOWNLOAD:
        this.downloadBookingAttachment(event.document);
        break;
      case DocumentAction.DELETE:
        this.deleteAttachment(event.document);
        break;
      default:
        console.log('Unknown action:', event);
    }
  }

  cargoTypeName(cargoTypeId: string): string {
    const cargoType = this.dataStoreService.getById<CargoTypeModel>(
      'cargoTypes',
      cargoTypeId
    );
    return cargoType?.name ?? '';
  }

  private generateBookingDetailsData(): void {
    const booking: BookingModel | null = this.booking();
    let totalQuantity: number = 0;
    let totalCubicMeters: number = 0;
    let totalWeight: number = 0;
    let totalRevenue: number = 0;

    if (!booking) {
      return;
    }

    //Substract id from uri
    const voyageId: number | null = booking.voyage
      ? Number(booking.voyage?.split('/').pop())
      : null;

    this.bookingsItems().forEach((item: BookingItemModel) => {
      const quantity = Number(item.quantity);
      const cubicMeters = Number(item.totalCubicMeters);
      const weight = Number(item.unitWeight) * quantity;
      totalQuantity += quantity;
      totalCubicMeters += cubicMeters;
      totalWeight += weight;
      totalRevenue += Number(item.revenue) ?? 0;
    });

    this.bookingDataList.set([
      {
        key: 'bookingCode',
        label: 'bookings.bookingCode',
        value: booking.bookingCode,
      },
      {
        key: 'billOfLading',
        label: 'bookings.billOfLading',
        value: booking.billOfLading,
      },
      {
        key: 'client',
        label: 'bookings.client',
        value:
          booking.clientDetails !== undefined
            ? clientNameTemplate(booking.clientDetails)
            : '',
      },
      {
        key: 'shipper',
        label: 'bookings.shipper',
        value: booking.shipper,
      },
      {
        key: 'consignee',
        label: 'bookings.consignee',
        value: booking.consignee,
      },
      {
        key: 'requiresTransshipment',
        label: 'bookings.requiresTransshipment',
        value: booking.requiresTransshipment
          ? this.translateService.instant('common.yes')
          : this.translateService.instant('common.no'),
      },
      {
        key: 'originPort',
        label: 'bookings.originPort',
        value:
          booking.originPortDetails !== undefined
            ? portNameTemplate(booking.originPortDetails)
            : '',
      },
      {
        key: 'destinationPort',
        label: booking.requiresTransshipment
          ? 'bookings.finalDestination'
          : 'bookings.destinationPort',
        value:
          booking.destinationPortDetails !== undefined
            ? portNameTemplate(booking.destinationPortDetails)
            : '',
      },
      {
        key: 'voyage',
        label: 'bookings.voyage',
        value:
          this.voyageDisplayName(booking.voyageDetails) ||
          (voyageId ? voyageId : ''),
      },
      {
        key: 'secondVoyage',
        label: 'bookings.secondVoyage',
        value: booking.secondVoyageDetails
          ? this.voyageDisplayName(booking.secondVoyageDetails)
          : this.translateService.instant('common.na'),
      },
      {
        key: 'transshipmentPort',
        label: 'bookings.transshipmentPort',
        value: booking.transshipmentPortDetails
          ? portNameTemplate(booking.transshipmentPortDetails)
          : this.translateService.instant('common.na'),
      },
      {
        key: 'requestDate',
        label: 'bookings.requestDate',
        value: DateUtils.formatDateOnly(booking.requestDate),
      },
      {
        key: 'paymentTerms',
        label: 'bookings.paymentTerms',
        value: getPaymentTermLabel(booking.paymentTerms ?? ''),
      },
      {
        key: 'totalQuantity',
        label: 'bookingsView.totalQuantity',
        value: totalQuantity,
        cssClasses: { value: 'c-tag u-fw-bold' },
      },
      {
        key: 'totalCubicMeters',
        label: 'bookingsView.totalCubicMeters',
        value: totalCubicMeters.toFixed(2),
        cssClasses: { value: 'c-tag u-fw-bold' },
      },
      {
        key: 'totalWeight',
        label: 'bookingsView.totalWeight',
        value: totalWeight.toFixed(2) + ' tons',
        cssClasses: { value: 'c-tag u-fw-bold' },
      },
      {
        key: 'revenue',
        label: 'bookingsView.revenue',
        value: `$ ${Number(totalRevenue ?? 0).toFixed(2)}`,
        cssClasses: { value: 'c-tag u-fw-bold' },
      },
    ]);
  }

  private generateAttachmentsList(): void {
    const documentItems: DocumentItem[] = [];
    this.booking()?.attachments?.forEach(
      (attachment: BookingAttachmentModel) => {
        documentItems.push({
          id: attachment.id,
          name: attachment.originalFileName ?? '',
          uploadedDate: attachment.createdAt ?? '',
          uploadedBy: '',
          // downloadUrl: string;
          fileSize: attachment.fileSize,
          fileType: attachment.mimeType,
          canDelete: true,
          canDownload: true,
        });
      }
    );
    this.bookingAttachments.set(documentItems);
  }

  private deleteAttachment(document: DocumentItem): void {
    this.confirmService
      .openDelete('dialog.confirmDeletionTitle', document.name)
      .subscribe((result) => {
        if (result) {
          this.loaderService.showLoader();
          this.bookingService
            .deleteBookingAttachment(this.bookingId(), document.id)
            .subscribe({
              next: (success: boolean) => {
                this.loaderService.hideLoader();
                if (success) {
                  ArrayUtility.remove(
                    this.booking()?.attachments ?? [],
                    document.id
                  );
                  this.generateAttachmentsList();
                  this.alertService.show({
                    type: AlertType.SUCCESS,
                    message: 'bookingsView.attachmentDeleted',
                    duration: 2000,
                    inBottom: true,
                  });
                }
              },
              error: (error: HttpErrorResponse) => {
                this.loaderService.hideLoader();
                this.alertService.show({
                  type: AlertType.ERROR,
                  message: error.error?.detail ?? `errors.generic`,
                  duration: 2000,
                  inBottom: true,
                });
              },
            });
        }
      });
  }

  private downloadBookingAttachment(document: DocumentItem): void {
    this.loaderService.showLoader();
    this.bookingService
      .downloadBookingAttachment(document.id, document.name)
      .subscribe({
        next: () => this.loaderService.hideLoader(),
        error: () => this.loaderService.hideLoader(),
      });
  }

  /**
   * Returns a human-readable display name for a voyage.
   * Delegates to `voyageNameTemplate` for consistent formatting across the UI.
   *
   * @param voyage - The voyage model to format.
   */
  voyageDisplayName(voyage?: VoyageModel): string {
    return voyageNameTemplate(voyage);
  }

  formatClientRateReference(bookingItem: BookingItemModel | null | undefined): string {
    if (!bookingItem) {
      return '-';
    }

    if (bookingItem.clientRateDetails) {
      const reference = bookingItem.clientRateDetails.getReference() || bookingItem.clientRateDetails.getId();
      return reference ? `${reference}${bookingItem.clientRateDetails.version ? ` · v${bookingItem.clientRateDetails.version}` : ''}` : '-';
    }

    if (bookingItem.clientRate) {
      const clientRateId = bookingItem.clientRate.split('/').pop();
      return clientRateId ? `Rate #${clientRateId}` : bookingItem.clientRate;
    }

    return '-';
  }

  private assignSuggestedClientRate(bookingItem: BookingItemModel, suggestedClientRate: any): void {
    if (!suggestedClientRate) {
      return;
    }

    if (typeof suggestedClientRate === 'object') {
      bookingItem.clientRateDetails = new ClientRateModel().fromJSON(suggestedClientRate);
      bookingItem.clientRate = clientRateUriTemplate(bookingItem.clientRateDetails.id?.toString());
      return;
    }

    bookingItem.clientRateDetails = undefined;
    const clientRateReference = suggestedClientRate?.toString();
    bookingItem.clientRate = clientRateReference?.includes('/api/client_rates/')
      ? clientRateReference
      : clientRateUriTemplate(clientRateReference);
  }

  private suggestRateForBookingItem(bookingItem: BookingItemModel): void {
    const booking = this.booking();
    if (!booking || !bookingItem) return;

    this.suggestingRevenueItemId.set(String(this.getBookingItemIdentifier(bookingItem)));

    // Set match params
    const queryParams: Record<string, string> = {
      client: booking.client ?? '',
      originPort: booking.originPort ?? '',
      destinationPort: booking.destinationPort ?? '',
      cargoType: bookingItem!.cargoType!,
      measurementUnit: 'per_unit', // Hardcoded for now,
      currency: booking.currency ?? '',
      quantity: String(bookingItem.quantity ?? 1),
      length: String(bookingItem.length ?? 0),
      width: String(bookingItem.width ?? 0),
      height: String(bookingItem.height ?? 0),
      unitWeight: String(bookingItem.unitWeight ?? 0),
      terms: bookingItem.terms ?? '',
    };

    // Fetch suggested rate
    this.apiService.fetchData(environment.API.CLIENT_RATES_MATCH_URI, queryParams)
      .pipe(
        finalize(() => {
          this.suggestingRevenueItemId.set(null);
        })
      )
      .subscribe({
      next: (response: ApiResponseModel) => {
        const suggestedRevenue = response?.data?.revenue;

        if (suggestedRevenue !== undefined && suggestedRevenue !== null) {
          this.assignSuggestedClientRate(bookingItem, response?.data?.clientRate ?? response?.data?.clientRateId);
          this.syncRevenueField(bookingItem, Number(suggestedRevenue).toFixed(2), true);

          this.alertService.show({
            type: AlertType.DEFAULT,
            message: this.translateService.instant('bookingsView.suggestedRate'),
              duration: 3000,
              inBottom: true
            });
            return;
          }

          this.alertService.show({
            type: AlertType.DEFAULT,
            message: this.translateService.instant('bookingsView.suggestionNotFound'),
            duration: 3000,
            inBottom: true,
          });
        },
        error: (error: HttpErrorResponse) => {
          this.alertService.show({
            type: AlertType.ERROR,
            message: error.error?.detail ?? 'errors.generic',
            duration: 3000,
            inBottom: true,
          });
        },
    });
  }

  private getBookingItemIdentifier(bookingItem: BookingItemModel | null | undefined): string | number {
    if (!bookingItem) {
      return '';
    }

    return typeof bookingItem.getId === 'function'
      ? bookingItem.getId()
      : bookingItem.id ?? '';
  }
}
